import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { LMSDataService } from "@/services/lms_data";
import { handle_error, handle_success } from "@/utils/wretch";
import { Outlet, RouteObject, defer, json } from "react-router-dom";

const AndpcRoute: RouteObject = {
    path: "andpc",
    element: (
        <Protected groups={["app_billing_admin", "team_finance", "team_support"]}>
            <Outlet />
        </Protected>
    ),
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/billing/andpc/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                if (!url.searchParams.has("include_closed_sessions")) {
                    url.searchParams.set("include_closed_sessions", "0");
                }
                const billing_sessions = await DataService.client
                    .url(`/billing/andpc/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return { billing_sessions };
            },
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    const content = await DataService.client.url(`/billing/andpc/create_from_reference/`).post(data).json();
                    return json({ success: true, content });
                } catch (error: any) {
                    const message = error.json?.message || error.message;
                    if (error.response?.status === 403) {
                        return json({ success: false, message: "Vous n'avez pas la permission de faire cette action." });
                    }
                    return json({ success: false, message });
                }
            }
        },
        {
            path: "a-creer",
            async lazy() {
                let { ToCreatePage } = await import("@/pages/billing/andpc/to_create");
                return { Component: ToCreatePage };
            },
            loader: async () => {
                const to_create = DataService.client.url(`/billing/andpc/to_create/`).get().json();
                return defer({ to_create });
            }
        },
        {
            path: "a-creer/:dpc_ref_action/:dpc_ref_session",
            async lazy() {
                let { ToCreateDetailPage } = await import("@/pages/billing/andpc/to_create_detail");
                return { Component: ToCreateDetailPage };
            },
            loader: async ({ params }) => {
                const to_create = DataService.client.url(`/billing/andpc/to_create/${params.dpc_ref_action}/${params.dpc_ref_session}/`).get().json();
                return defer({ to_create });
            },
            action: async ({ params, request }) => {
                const data = await request.json();
                try {
                    const content = await DataService.client.url(`/billing/andpc/to_create/${params.dpc_ref_action}/${params.dpc_ref_session}/build/`).post(data).json();
                    return json({ success: true, content: content });
                } catch (error: any) {
                    try {
                        if (error.response) {
                            switch (error.response.status) {
                                case 400:
                                    return json({ success: false, message: error.message });
                                case 401:
                                    return json({ success: false, message: "Veuillez vous reconnecter." });
                                case 403:
                                    return json({ success: false, message: "Vous n'avez pas la permission de faire cette action." });
                                case 404:
                                    return json({ success: false, message: "Cette action n'existe pas, veuillez contacter l'équipe technique." });
                                default:
                                    return json({
                                        success: false,
                                        message: `Veuillez contacter l'équipe technique (${error.response.status} ${error.response.statusText}).`
                                    });
                            }
                        }
                    } catch (_error) {
                        return json({ success: false, message: "Veuillez contacter l'équipe technique." }, { status: 500 });
                    }
                }
            }
        },
        {
            path: "participants/:action/:session",
            loader: async ({ params }) => {
                const action = params.action;
                const session = params.session;
                try {
                    const participants = await DataService.client
                        .url(`/registration/admin/funding_organism_registrations/?page_size=1000&funding_organism_session_reference=${action}-${session}`)
                        .get()
                        .json();
                    return handle_success(participants);
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: "jobs/:action/:session",
            loader: async ({ params, request }) => {
                try {
                    const url = new URL(request.url);
                    const year = url.searchParams.get("year");
                    const yearFilter = year ? `&year=${year}` : "";
                    const jobs = await DataService.client
                        .url(`/billing/jobs/?ref_action=${params.action}&ref_session=${params.session}${yearFilter}&page_size=100`)
                        .get()
                        .json();
                    return handle_success(jobs);
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: "jobs/:jobId/retry",
            action: async ({ params }) => {
                try {
                    const job = await DataService.client.url(`/billing/jobs/${params.jobId}/retry/`).post({}).json();
                    return handle_success(job);
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: "details/:action/:session",
            loader: async ({ params }) => {
                const action = params.action;
                const session = params.session;
                try {
                    const details = await LMSDataService.client.url(`/andpc/actions/${action}/${session}/details/`).get().json();
                    return handle_success(details);
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            path: ":sessionId",
            async lazy() {
                let { DetailPage } = await import("@/pages/billing/andpc/details");
                return { Component: DetailPage };
            },
            loader: async ({ params }) => {
                const sessionId = params.sessionId;
                const billing_session = await DataService.client.url(`/billing/andpc/${sessionId}/`).get().json();
                const billing_session_data = await DataService.client.url(`/billing/andpc/${sessionId}/data/`).get().json();
                const billing_session_submissions = await DataService.client.url(`/billing/andpc/${sessionId}/submissions/`).get().json();
                return { billing_session, billing_session_data, billing_session_submissions };
            },
            action: async ({ params, request }) => {
                const sessionId = params.sessionId;
                const data = await request.json();
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                if (action) {
                    try {
                        const content = await DataService.client.url(`/billing/andpc/${sessionId}/${action}/`).post(data).json();
                        return handle_success(content);
                    } catch (error) {
                        return handle_error(error);
                    }
                } else {
                    try {
                        const content = await DataService.client.url(`/billing/andpc/${sessionId}/`).patch(data).json();
                        return handle_success(content);
                    } catch (error) {
                        return handle_error(error);
                    }
                }
            },
            children: [
                {
                    path: ":submissionId",
                    loader: async ({ params }) => {
                        const submissionId = params.submissionId;
                        const billing_submission = await DataService.client.url(`/billing/andpc_submissions/${submissionId}/`).get().json();
                        return { billing_submission };
                    },
                    action: async ({ params, request }) => {
                        const submissionId = params.submissionId;
                        const data = await request.json();
                        const url = new URL(request.url);
                        const action = url.searchParams.get("action");
                        if (action) {
                            try {
                                const content = await DataService.client.url(`/billing/andpc_submissions/${submissionId}/${action}/`).post(data).json();
                                return handle_success(content);
                            } catch (error) {
                                return handle_error(error);
                            }
                        } else {
                            try {
                                const content = await DataService.client.url(`/billing/andpc_submissions/${submissionId}/`).patch(data).json();
                                return handle_success(content);
                            } catch (error) {
                                return handle_error(error);
                            }
                        }
                    }
                }
            ]
        }
    ]
};
export default AndpcRoute;
